import { useState, useEffect, useCallback, type SyntheticEvent } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Header as CwdsHeader } from "@ingka-group-digital/cwds-react-header";
import type { NavItemModelReact } from "@ingka-group-digital/cwds-react-shared-models";
import Button from "@ingka/button";
import pathArrowPairSquarepath from "@ingka/ssr-icon/paths/arrow-pair-squarepath";
import pathFlag from "@ingka/ssr-icon/paths/flag";
import pathFlagFilled from "@ingka/ssr-icon/paths/flag-filled";
import pathFlame from "@ingka/ssr-icon/paths/flame";
import pathSnowflake from "@ingka/ssr-icon/paths/snowflake";
import pathInformationCircle from "@ingka/ssr-icon/paths/information-circle";
import { getKeepWarm, setKeepWarm, getKeepWarmExpiry, onKeepWarmChange } from "../api/connectionStatus";
import { buildSharedQueryString } from "../hooks/useSharedParams";
import { useIsMobile } from "../hooks/useIsMobile";
import { BottomNav } from "./BottomNav";
import { HelpModal } from "./HelpModal";
import { UserMenu } from "./UserMenu";
import styles from "./Header.module.css";

const isProd = !window.location.hostname.includes(".dev") && !window.location.hostname.includes("localhost");
const envLabel = isProd ? "" : "dev";

function formatRemaining(expiry: number | null): string {
    if (!expiry) return "";
    const ms = expiry - Date.now();
    if (ms <= 0) return "";
    const totalMin = Math.ceil(ms / 60_000);
    const h = Math.floor(totalMin / 60);
    const m = totalMin % 60;
    if (h > 0) return `${h}h${m > 0 ? ` ${m}m` : ""}`;
    return `${m}m`;
}

export function Header() {
    const location = useLocation();
    const navigate = useNavigate();
    const isMobile = useIsMobile();
    const currentPage = location.pathname.startsWith("/analysis") ? "analysis"
        : location.pathname.startsWith("/top") || location.pathname === "/" ? "top"
        : null;
    const [warm, setWarm] = useState(() => getKeepWarm());
    const [remaining, setRemaining] = useState(() => formatRemaining(getKeepWarmExpiry()));
    const [updateAvailable, setUpdateAvailable] = useState(false);
    const [showHelp, setShowHelp] = useState(false);

    useEffect(() => {
        const unsub = onKeepWarmChange((w) => {
            setWarm(w);
            setRemaining(formatRemaining(getKeepWarmExpiry()));
        });
        return unsub;
    }, []);

    // Update countdown every minute while warm
    useEffect(() => {
        if (!warm) return;
        setRemaining(formatRemaining(getKeepWarmExpiry()));
        const id = setInterval(() => {
            setRemaining(formatRemaining(getKeepWarmExpiry()));
        }, 60_000);
        return () => clearInterval(id);
    }, [warm]);

    // Detect new builds: fetch index.html when the tab regains visibility.
    // We only poll on visibilitychange so background tabs don't keep the
    // backend warm and prevent Cloud Run from scaling to zero.
    useEffect(() => {
        let initialEtag: string | null = null;
        let inFlight = false;
        const MIN_INTERVAL_MS = 5 * 60_000;
        let lastCheck = 0;

        const fetchEtag = async (): Promise<string | null> => {
            try {
                const res = await fetch("/", { method: "HEAD", cache: "no-store" });
                if (!res.ok) return null;
                return res.headers.get("etag") || res.headers.get("last-modified");
            } catch {
                return null;
            }
        };

        const check = async () => {
            if (inFlight) return;
            const now = Date.now();
            if (now - lastCheck < MIN_INTERVAL_MS) return;
            lastCheck = now;
            inFlight = true;
            try {
                const tag = await fetchEtag();
                if (!tag) return;
                if (initialEtag === null) {
                    initialEtag = tag;
                } else if (tag !== initialEtag) {
                    setUpdateAvailable(true);
                }
            } finally {
                inFlight = false;
            }
        };

        // Capture the baseline once the tab is first visible.
        if (document.visibilityState === "visible") void check();

        const onVis = () => {
            if (document.visibilityState === "visible") void check();
        };
        document.addEventListener("visibilitychange", onVis);
        window.addEventListener("focus", onVis);

        // Dev: Vite plugin pushes a `build-info:updated` event when git HEAD
        // moves, so the button shows up locally on commit just like in prod.
        const hot = (import.meta as ImportMeta).hot;
        const onHmr = () => setUpdateAvailable(true);
        hot?.on("build-info:updated", onHmr);

        return () => {
            document.removeEventListener("visibilitychange", onVis);
            window.removeEventListener("focus", onVis);
            hot?.off("build-info:updated", onHmr);
        };
    }, []);

    const toggleWarm = () => {
        setKeepWarm(!warm);
    };

    // In dev the " · dev" suffix would push the user icon off-screen on narrow
    // devices, so abbreviate to "MCC · dev"; prod shows the full name.
    const title = envLabel ? `MCC · ${envLabel}` : "Most Commercial Combinations";

    const navItems: NavItemModelReact[] = [
        { label: "Top Combinations", href: "/top", isActive: currentPage === "top" },
        { label: "Analysis", href: "/analysis", isActive: currentPage === "analysis" },
    ];

    // Build the shared query string from the LIVE URL at click time, not a
    // possibly-stale render snapshot, so a recent anchor switch is never
    // dropped on page change.
    const handleRedirect = useCallback((href: string, _target: string, event: SyntheticEvent) => {
        event.preventDefault();
        const path = href.split("?")[0];
        const qs = buildSharedQueryString(new URLSearchParams(window.location.search));
        navigate(`${path}${qs}`);
    }, [navigate]);

    // Clicking the title goes home; Cmd-click opens feature flags in prod.
    const handleTitleClick = useCallback((event: SyntheticEvent) => {
        if ((event as React.MouseEvent).metaKey) {
            navigate("/features");
            return;
        }
        navigate("/");
    }, [navigate]);

    const goTo = useCallback((path: "/top" | "/analysis") => {
        const qs = buildSharedQueryString(new URLSearchParams(window.location.search));
        navigate(`${path}${qs}`);
    }, [navigate]);

    const onFeaturesPage = location.pathname.startsWith("/features");
    const toggleFeatures = useCallback(() => {
        if (location.pathname.startsWith("/features")) {
            navigate(-1);
        } else {
            navigate("/features");
        }
    }, [location.pathname, navigate]);

    const utilities = (
        <>
            {!isMobile && currentPage && (
                <Button
                    type="tertiary"
                    size="xsmall"
                    iconOnly
                    inverseTheme
                    ssrIcon={pathInformationCircle}
                    onClick={() => setShowHelp(true)}
                    title={`Help — about ${currentPage === "analysis" ? "Analysis" : "Top Combinations"}`}
                />
            )}
            {!isMobile && !isProd && (
                <Button
                    type="tertiary"
                    size="xsmall"
                    inverseTheme
                    ssrIcon={warm ? pathFlame : pathSnowflake}
                    className={`${styles.scalingToggle} ${warm ? styles.scalingOn : styles.scalingOff}`}
                    onClick={toggleWarm}
                    title={warm ? `Keep-warm active — auto-off in ${remaining || "< 1m"}. Click to disable.` : "Keep-warm disabled. Server may scale to zero when idle. Click to enable for 4h."}
                    text={warm ? remaining || "< 1m" : "Idle"}
                />
            )}
            {!isMobile && !isProd && (
                <Button
                    id="feature-flags-button"
                    type="tertiary"
                    size="xsmall"
                    iconOnly
                    inverseTheme
                    ssrIcon={onFeaturesPage ? pathFlagFilled : pathFlag}
                    onClick={toggleFeatures}
                    title="Feature flags"
                />
            )}
            {updateAvailable && !isMobile && (
                <Button
                    type="tertiary"
                    size="xsmall"
                    iconOnly
                    inverseTheme
                    ssrIcon={pathArrowPairSquarepath}
                    className={styles.updateBtn}
                    onClick={() => window.location.reload()}
                    title="A new version is available — click to reload"
                />
            )}
            <UserMenu />
        </>
    );

    return (
        <>
            <CwdsHeader
                className={styles.header}
                title={title}
                appLink="/"
                onTitleClick={handleTitleClick}
                navItems={navItems}
                headerNavMode="HEADER_LINKS"
                isShowingBottomNav={isMobile}
                isAppSwitcherShown={false}
                narrowContent={false}
                onRedirectChange={handleRedirect}
                utilities={utilities}
            />
            {isMobile && (
                <BottomNav
                    currentPage={currentPage}
                    onNavigate={goTo}
                    onHelp={() => setShowHelp(true)}
                    isProd={isProd}
                    warm={warm}
                    remaining={remaining}
                    onToggleWarm={toggleWarm}
                    onSettings={toggleFeatures}
                    settingsActive={onFeaturesPage}
                    updateAvailable={updateAvailable}
                    onReload={() => window.location.reload()}
                />
            )}
            {showHelp && <HelpModal page={currentPage} onClose={() => setShowHelp(false)} />}
        </>
    );
}
